import { Button, Dialog } from '@artosera/ui';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FermetureGardee {
  /** À passer au `onOpenChange` du dialogue et aux boutons « Annuler ». */
  readonly onOpenChange: (ouvert: boolean) => void;
  /** La demande de confirmation, à rendre à côté du dialogue. */
  readonly confirmation: JSX.Element;
}

/**
 * Fermer un dialogue de saisie (croix, Échap, clic hors de la fenêtre,
 * « Annuler ») avec des champs remplis perd ce qui a été tapé : on demande
 * d'abord confirmation. Sans modification, la fermeture est immédiate. La
 * réussite d'un envoi ferme le dialogue par la prop `onOpenChange` du parent,
 * sans passer par ici.
 */
export function useFermetureGardee(
  modifie: boolean,
  onOpenChange: (ouvert: boolean) => void,
): FermetureGardee {
  const { t } = useTranslation(['regie', 'commun']);
  const [demande, setDemande] = useState(false);

  const surChangement = useCallback(
    (ouvert: boolean) => {
      if (!ouvert && modifie) {
        setDemande(true);
        return;
      }
      onOpenChange(ouvert);
    },
    [modifie, onOpenChange],
  );

  const confirmation = (
    <Dialog
      open={demande}
      onOpenChange={setDemande}
      title={t('regie:garde.fermerDialogue')}
      description={t('regie:garde.fermerTexte')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={() => setDemande(false)}>
            {t('regie:garde.continuerSaisie')}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setDemande(false);
              onOpenChange(false);
            }}
          >
            {t('regie:garde.quitter')}
          </Button>
        </>
      }
    />
  );

  return { onOpenChange: surChangement, confirmation };
}
